// Reglas de órdenes para decidir qué ofrece la interfaz. Replica las RPCs save_order y
// transition_order; el servidor sigue siendo la autoridad.
import { isFinalStatus, isInProgress, type OrderStatus } from './status.ts';
import { isStaff, type Role } from './roles.ts';

export type WorkType = 'repair' | 'installation' | 'no_warranty';

export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  repair: 'Garantía',
  installation: 'Instalación',
  no_warranty: 'Sin garantía',
};

export interface OrderContext {
  role: Role;
  userId: string;
  status: OrderStatus;
  assignedTo: string | null;
}

/** Destinos válidos desde el estado actual para este usuario (§5). */
export function allowedTargets(ctx: OrderContext): OrderStatus[] {
  const staff = isStaff(ctx.role);
  if (!staff && ctx.assignedTo !== ctx.userId) return [];
  switch (ctx.status) {
    case 'ingreso_os':
      return staff ? ['ruta', 'anulacion'] : ['ruta'];
    case 'ruta':
      return staff ? ['chequeo_repuestos', 'listo', 'anulacion'] : ['chequeo_repuestos', 'listo'];
    case 'chequeo_repuestos':
      return staff ? ['repuesto_despachado', 'anulacion'] : [];
    case 'repuesto_despachado':
      return staff ? ['ruta', 'anulacion'] : ['ruta'];
    case 'listo':
      // Revisión: objetar (devolver al técnico con observaciones), cerrar o anular.
      return staff ? ['objetada', 'cerrada', 'anulacion'] : [];
    case 'objetada':
      // El técnico corrige en una nueva visita.
      return staff ? ['ruta', 'anulacion'] : ['ruta'];
    default:
      return [];
  }
}

/** La orden puede pasar a Ruta (desde Ingreso OS, Repuesto Despachado u Objetada): "Actualizar OS" abre el
 *  asistente y la orden pasa a Ruta al guardar el diagnóstico (start_visit), no antes. */
export function canStartVisit(ctx: OrderContext): boolean {
  return allowedTargets(ctx).includes('ruta');
}

/** "Actualizar OS": el asistente que lleva la orden de Ruta a Chequeo o a Listo. */
export function canRegisterVisit(ctx: OrderContext): boolean {
  return ctx.status === 'ruta' && allowedTargets(ctx).includes('listo');
}

/** Revisión de una orden Listo: cerrar u objetar (solo staff). */
export function canReview(ctx: OrderContext): boolean {
  return isStaff(ctx.role) && ctx.status === 'listo';
}

export function canDispatchParts(ctx: OrderContext): boolean {
  return allowedTargets(ctx).includes('repuesto_despachado');
}

export function canAnnul(ctx: OrderContext): boolean {
  return allowedTargets(ctx).includes('anulacion');
}

/** Cerrada y Anulación son de solo lectura para todos: para recuperar una orden se crea otra con sus datos. */
export function canEditRequest(ctx: OrderContext): boolean {
  return isStaff(ctx.role) && !isFinalStatus(ctx.status);
}

/** La asignación queda congelada en estados definitivos (C3). */
export function canEditAssignment(ctx: OrderContext): boolean {
  return isStaff(ctx.role) && !isFinalStatus(ctx.status);
}

/** Staff: salvo Cerrado y Anulado (incluye Listo en revisión). Técnico: solo su orden en Ruta. */
export function canEditTechnical(ctx: OrderContext): boolean {
  if (isStaff(ctx.role)) return !isFinalStatus(ctx.status);
  return ctx.assignedTo === ctx.userId && ctx.status === 'ruta';
}

/** Staff: salvo Cerrado y Anulado. Técnico: su orden mientras está en curso (en Listo ya está en revisión). */
export function canManageEvidence(ctx: OrderContext): boolean {
  if (isStaff(ctx.role)) return !isFinalStatus(ctx.status);
  return ctx.assignedTo === ctx.userId && isInProgress(ctx.status);
}

export function canCopy(ctx: OrderContext): boolean {
  return isStaff(ctx.role) && isFinalStatus(ctx.status);
}

/** Orden en curso sin técnico: no puede avanzar (C4). */
export function needsTechnician(ctx: OrderContext): boolean {
  return ctx.assignedTo === null && !isFinalStatus(ctx.status) && ctx.status !== 'ingreso_os';
}

/** Resultado de una visita ("Actualizar OS"): los dos primeros cambian el estado; "seguimiento" deja
 *  la orden en Ruta. Cada visita queda numerada en el historial (register_visit). */
export type VisitOutcome = 'chequeo_repuestos' | 'listo' | 'seguimiento';

export const VISIT_OUTCOME_LABELS: Record<VisitOutcome, string> = {
  chequeo_repuestos: 'Solicitar repuestos',
  listo: 'Trabajo terminado',
  seguimiento: 'Requiere otra visita',
};

export type RequirementTarget = 'chequeo_repuestos' | 'listo' | 'cerrada';

/** Requisitos que revisa el servidor para cada destino (private.missing_requirements), en orden de trabajo. */
export function requirementKeys(target: RequirementTarget): string[] {
  return [
    'technician',
    'model',
    'serial',
    'diagnosis',
    ...(target === 'chequeo_repuestos' ? [] : ['solution']),
    'media_ready',
    'media_pending',
  ];
}

/** Aviso de los diálogos de cambio de estado del detalle; el asistente usa la descripción de cada proceso. */
export const TARGET_CONFIRMATIONS: Partial<Record<OrderStatus, string>> = {
  cerrada: 'Cierre OS es definitivo: Se guardará la información de la orden y la orden no podrá modificarse.',
  anulacion: 'La anulación es definitiva: la orden no podrá reactivarse.',
  repuesto_despachado: 'Se notificará al técnico asignado que el repuesto fue despachado.',
};

/** Equipo, marca y modelo en una línea, como en la lista de órdenes: "LAVADORA LG · WM22". */
export function equipmentLabel(order: { equipment: string; brand: string; model: string }): string {
  return `${order.equipment} ${order.brand}${order.model ? ` · ${order.model}` : ''}`;
}

/** Enlace tel: con solo dígitos y un + inicial; null si no parece un teléfono. */
export function telHref(phone: string): string | null {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 6 || digits.length > 15) return null;
  return `tel:${trimmed.startsWith('+') ? '+' : ''}${digits}`;
}
